import { useState, useCallback, useMemo, useRef } from 'react';
import { getCSRFToken } from '../utils/csrf';

type RelatedOrder = Record<string, number[]>;

interface EditableFields {
  title: string;
  description: string;
}

interface FormatPrice {
  id: number;
  price: number;
}

interface UseAdminContentEditOptions {
  productId: number;
  initialValues: EditableFields;
  initialFormatPrices: FormatPrice[];
  onSaved?: () => void;
}

interface UseAdminContentEditReturn {
  isEditMode: boolean;
  toggleEditMode: () => void;
  editedValues: Partial<EditableFields>;
  isDirty: boolean;
  isFieldDirty: (field: keyof EditableFields) => boolean;
  getDisplayValue: (field: keyof EditableFields) => string;
  updateField: (field: keyof EditableFields, value: string) => void;
  // Format prices
  editedFormatPrices: Record<number, number>;
  updateFormatPrice: (formatId: number, price: number) => void;
  getFormatPrice: (formatId: number) => number | null;
  isFormatPriceDirty: (formatId: number) => boolean;
  // Image
  pendingImage: File | null;
  imagePreviewUrl: string | null;
  setImage: (file: File) => void;
  // Related products order
  relatedOrder: RelatedOrder | null;
  setRelatedOrder: (order: RelatedOrder) => void;
  isRelatedOrderDirty: boolean;
  save: () => Promise<void>;
  cancel: () => void;
  isSaving: boolean;
  saveStatus: 'idle' | 'success' | 'error';
  errorMessage: string | null;
}

export function useAdminContentEdit({
  productId,
  initialValues,
  initialFormatPrices,
  onSaved,
}: UseAdminContentEditOptions): UseAdminContentEditReturn {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState<Partial<EditableFields>>({});
  const [editedFormatPrices, setEditedFormatPrices] = useState<Record<number, number>>({});
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [relatedOrder, setRelatedOrderState] = useState<RelatedOrder | null>(null);
  const relatedOrderDirtyRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const initialPriceMap = useMemo(() => {
    const map: Record<number, number> = {};
    for (const fp of initialFormatPrices) {
      map[fp.id] = fp.price;
    }
    return map;
  }, [initialFormatPrices]);

  const isFieldsDirty = useMemo(() => {
    return Object.keys(editedValues).some(
      (key) => editedValues[key as keyof EditableFields] !== initialValues[key as keyof EditableFields]
    );
  }, [editedValues, initialValues]);

  const isFormatsDirty = useMemo(() => {
    return Object.entries(editedFormatPrices).some(
      ([id, price]) => price !== initialPriceMap[Number(id)]
    );
  }, [editedFormatPrices, initialPriceMap]);

  const isRelatedOrderDirty = relatedOrderDirtyRef.current;
  const isDirty = isFieldsDirty || isFormatsDirty || pendingImage !== null || isRelatedOrderDirty;

  const setRelatedOrder = useCallback((order: RelatedOrder) => {
    setRelatedOrderState(order);
    relatedOrderDirtyRef.current = true;
  }, []);

  const isFieldDirty = useCallback(
    (field: keyof EditableFields) => {
      return field in editedValues && editedValues[field] !== initialValues[field];
    },
    [editedValues, initialValues]
  );

  const getDisplayValue = useCallback(
    (field: keyof EditableFields) => {
      return field in editedValues ? (editedValues[field] ?? '') : (initialValues[field] ?? '');
    },
    [editedValues, initialValues]
  );

  const updateField = useCallback((field: keyof EditableFields, value: string) => {
    setEditedValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateFormatPrice = useCallback((formatId: number, price: number) => {
    setEditedFormatPrices((prev) => ({ ...prev, [formatId]: price }));
  }, []);

  const getFormatPrice = useCallback(
    (formatId: number): number | null => {
      if (formatId in editedFormatPrices) return editedFormatPrices[formatId];
      return initialPriceMap[formatId] ?? null;
    },
    [editedFormatPrices, initialPriceMap]
  );

  const isFormatPriceDirty = useCallback(
    (formatId: number) => {
      return formatId in editedFormatPrices && editedFormatPrices[formatId] !== initialPriceMap[formatId];
    },
    [editedFormatPrices, initialPriceMap]
  );

  const setImage = useCallback((file: File) => {
    setPendingImage(file);
    const url = URL.createObjectURL(file);
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
  }, []);

  const clearImage = useCallback(() => {
    setPendingImage(null);
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const save = useCallback(async () => {
    if (!isDirty) return;

    setIsSaving(true);
    setSaveStatus('idle');
    setErrorMessage(null);

    // Changed text fields
    const changedFields: Record<string, string> = {};
    for (const key of Object.keys(editedValues) as (keyof EditableFields)[]) {
      if (editedValues[key] !== initialValues[key]) {
        changedFields[key] = editedValues[key]!;
      }
    }

    // Changed format prices
    const changedFormats = Object.entries(editedFormatPrices)
      .filter(([id, price]) => price !== initialPriceMap[Number(id)])
      .map(([id, price]) => ({ id: Number(id), price }));

    const hasImage = pendingImage !== null;

    try {
      let response: Response;

      if (hasImage) {
        // Use FormData when there's an image
        const formData = new FormData();
        formData.append('_method', 'PATCH');
        formData.append('image', pendingImage);
        for (const [key, value] of Object.entries(changedFields)) {
          formData.append(key, value);
        }
        if (changedFormats.length > 0) {
          formData.append('formats', JSON.stringify(changedFormats));
        }
        if (relatedOrder && relatedOrderDirtyRef.current) {
          formData.append('related_products_order', JSON.stringify(relatedOrder));
        }
        response = await fetch(`/admin/products/${productId}/inline-edit`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-XSRF-TOKEN': getCSRFToken(),
          },
          credentials: 'same-origin',
          body: formData,
        });
      } else {
        // Use JSON when no image
        const payload: Record<string, unknown> = { ...changedFields };
        if (changedFormats.length > 0) {
          payload.formats = changedFormats;
        }
        if (relatedOrder && relatedOrderDirtyRef.current) {
          payload.related_products_order = relatedOrder;
        }
        response = await fetch(`/admin/products/${productId}/inline-edit`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-XSRF-TOKEN': getCSRFToken(),
          },
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        });
      }

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.message || 'Error al guardar');
        setSaveStatus('error');
        return;
      }

      setSaveStatus('success');
      setEditedValues({});
      setEditedFormatPrices({});
      clearImage();
      setRelatedOrderState(null);
      relatedOrderDirtyRef.current = false;
      onSaved?.();
      setTimeout(() => {
        setSaveStatus('idle');
        setIsEditMode(false);
      }, 1500);
    } catch {
      setErrorMessage('Error de conexión');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 4000);
    } finally {
      setIsSaving(false);
    }
  }, [isDirty, editedValues, editedFormatPrices, pendingImage, initialValues, initialPriceMap, productId, onSaved, clearImage]);

  const resetAll = useCallback(() => {
    setEditedValues({});
    setEditedFormatPrices({});
    clearImage();
    setRelatedOrderState(null);
    relatedOrderDirtyRef.current = false;
    setSaveStatus('idle');
    setErrorMessage(null);
  }, [clearImage]);

  const cancel = useCallback(() => {
    resetAll();
    setIsEditMode(false);
  }, [resetAll]);

  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => {
      if (prev) resetAll();
      return !prev;
    });
  }, [resetAll]);

  return {
    isEditMode,
    toggleEditMode,
    editedValues,
    isDirty,
    isFieldDirty,
    getDisplayValue,
    updateField,
    editedFormatPrices,
    updateFormatPrice,
    getFormatPrice,
    isFormatPriceDirty,
    pendingImage,
    imagePreviewUrl,
    setImage,
    relatedOrder,
    setRelatedOrder,
    isRelatedOrderDirty,
    save,
    cancel,
    isSaving,
    saveStatus,
    errorMessage,
  };
}
